import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";

const PER_PAGE = 10;

const PROPOSAL_ENQUIRY_LABELS = {
  submittedOn: "Submitted On",
  proposalRegisteredOn: "Proposal Registered On",
  proposalNumber: "Proposal Number",
  underwrittingDecision: "Underwriting Decision",
  policyNumber: "Policy Number",
  installmentPremium: "Installment Premium",
  proposalStatus: "Proposal Status",
};

const formatEnquiryLabel = (key) =>
  PROPOSAL_ENQUIRY_LABELS[key] ||
  key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

const MyApplications = () => {
  const history = useHistory();
  const token = localStorage.getItem("authToken");
  const user = useSelector((state) => state?.user);
  const userType = user?.loginuser?.user_type;
  const roleId = Number(user?.loginuser?.role_id);
  const isPolicyHolder = roleId === 5;
  // OTP customer login uses customer-api JWT; policy holder uses user-api (role 5).
  const usesCustomerApi = userType === "Customer" && !isPolicyHolder;

  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [selectedReferenceNo, setSelectedReferenceNo] = useState(null);

  const fetchApplications = useCallback(
    async (page = 1) => {
      if (!token) {
        setError("Please log in to view your applications.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const endpoint = usesCustomerApi
          ? "/api/policy-applications-customer-list"
          : "/api/policy-applications-list";

        const response = await axiosNewInstance.post(
          endpoint,
          { page, per_page: PER_PAGE },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const policiesPayload = response?.data?.data?.policies;
        setApplications(policiesPayload?.data || []);
        setCurrentPage(policiesPayload?.current_page || 1);
        setTotalPages(policiesPayload?.last_page || 1);
        setTotalRecords(policiesPayload?.total ?? 0);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch applications."
        );
        setApplications([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [token, usesCustomerApi]
  );

  useEffect(() => {
    if (token) {
      fetchApplications(currentPage);
    } else {
      history.push("/login");
    }
  }, [token, currentPage, fetchApplications, history]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsError(null);
    setDetailsData(null);
    setSelectedReferenceNo(null);
  };

  const handleView = async (refUniqueId) => {
    const referenceNumber = (refUniqueId || "").trim();
    if (!referenceNumber || referenceNumber === "—") {
      setSelectedReferenceNo(null);
      setDetailsError("Reference number is not available for this application.");
      setDetailsData(null);
      setDetailsOpen(true);
      return;
    }

    if (!token) {
      history.push("/login");
      return;
    }

    setSelectedReferenceNo(referenceNumber);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData(null);

    const endpoint = usesCustomerApi
      ? "/api/proposal-enquiry-customer"
      : "/api/proposal-enquiry";

    try {
      const response = await axiosNewInstance.post(
        endpoint,
        { referenceNumber },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.status === 1 && response?.data?.data) {
        setDetailsData(response.data.data);
      } else {
        setDetailsError(
          response?.data?.Error ||
            response?.data?.message ||
            "Unable to fetch proposal enquiry."
        );
      }
    } catch (err) {
      const data = err?.response?.data;
      setDetailsError(
        data?.Error ||
          data?.Message ||
          data?.message ||
          err?.message ||
          "Unable to fetch proposal enquiry."
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const renderEnquiryRows = () => {
    if (!detailsData || typeof detailsData !== "object") return null;

    const entries = Object.entries(detailsData).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    );

    if (entries.length === 0) {
      return (
        <p className="text-muted mb-0">No proposal details returned.</p>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0">
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <th
                  scope="row"
                  style={{
                    width: "40%",
                    background: "#f8f9fa",
                    textTransform: "none",
                  }}
                >
                  {formatEnquiryLabel(key)}
                </th>
                <td>
                  {typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <div className="card p-3 shadow-sm">
        <h5 className="font-weight-bold fs-4 my-3 text-left">
          <LangData keyname="label_my_applications" />
          {!loading && !error && (
            <span className="text-muted fs-6 fw-normal ms-2">
              ({totalRecords} total)
            </span>
          )}
        </h5>

        {loading ? (
          <div className="text-center p-5">
            <span className="spinner-border spinner-border-lg" />
            <p className="mt-2 text-muted">Loading your applications...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger py-2">{error}</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover text-left">
                <thead>
                  <tr>
                    <th scope="col">
                      <LangData keyname="label_serial" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_name" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_policy_id" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_ref_id" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_mobile" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_product_name" />
                    </th>
                    <th scope="col">
                      <LangData keyname="label_action" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length > 0 ? (
                    applications.map((item, index) => (
                      <tr key={item?.id}>
                        <td>{index + 1 + (currentPage - 1) * PER_PAGE}</td>
                        <td>
                          {item?.customer?.first_name || ""}{" "}
                          {item?.customer?.last_name || ""}
                        </td>
                        <td>{item?.sap_policy_id || "—"}</td>
                        <td>{item?.ref_unique_id || "—"}</td>
                        <td>{item?.customer?.mobile || "N/A"}</td>
                        <td>{item?.policy?.name || item?.plan || "N/A"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleView(item?.ref_unique_id)}
                            title="View proposal enquiry"
                          >
                            <i className="fas fa-eye" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No applications found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {applications.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="text-muted">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {detailsOpen && (
        <>
          <div className="modal-backdrop fade show" />
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Proposal Enquiry
                    {selectedReferenceNo ? ` — ${selectedReferenceNo}` : ""}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={handleCloseDetails}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {detailsLoading && (
                    <div className="text-center p-3">
                      <span className="spinner-border spinner-border-sm mr-2" />
                      Loading proposal enquiry...
                    </div>
                  )}
                  {detailsError && !detailsLoading && (
                    <div className="alert alert-danger py-2 mb-0">
                      {detailsError}
                    </div>
                  )}
                  {!detailsLoading && !detailsError && renderEnquiryRows()}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MyApplications;
